import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { useAuth } from "@/context/AuthContext";
import AccessDenied from "@/components/AccessDenied";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Check, Sparkles, Building2, Users, Receipt, Download, AlertTriangle, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Billing() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const PLAN_LABELS = { individual_monthly: t("billing.planIndividualMonthly"), individual_yearly: t("billing.planIndividualYearly"), organization: t("billing.planOrganization") };
  const canManageBilling = user?.account_type !== "organization" || user?.is_org_admin === true || user?.is_primary_admin === true;
  const [data, setData] = useState(null);
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);
  const [dunning, setDunning] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [country, setCountry] = useState("");

  const cancelPlan = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success(t("billing.cancelScheduled"));
      setCancelOpen(false);
      if (refresh) await refresh();
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setCancelBusy(false); }
  };

  const load = async () => {
    try {
      const res = await api.get("/billing/plans"); setData(res.data); if (res.data.member_count) setSeats(res.data.member_count);
      api.get("/billing/history").then((r) => setHistory(r.data)).catch(() => {});
      if (user?.account_type === "organization") {
        api.get("/billing/org_dunning").then((r) => setDunning(r.data)).catch(() => {});
      }
    }
    catch (err) { toast.error(apiError(err)); }
  };

  const openReceipt = async (t) => {
    try {
      const res = await api.get(`/billing/receipt/${t.session_id}`);
      window.open(res.data.url, "_blank", "noopener");
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { if (canManageBilling) load(); }, [canManageBilling]);

  const proceedCheckout = async (plan) => {
    setBusy(plan);
    try {
      const res = await api.post("/billing/checkout", {
        plan, seats: plan === "organization" ? seats : undefined,
        origin_url: window.location.origin, country,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(""); }
  };
  const checkout = (plan) => { if (!country) return; proceedCheckout(plan); };

  if (user && !canManageBilling) return <AccessDenied />;
  if (!data) return <p className="text-slatebody">{t("common.loading")}</p>;
  const isOrgAdmin = data.is_org_admin === true;
  const isActive = data.subscription_status === "active";
  const seatPrice = seats <= 20 ? 48 : seats <= 100 ? 40 : 36;
  const cur = country ? (country === "CA" ? "CAD" : "USD") : null;
  const sym = cur === "CAD" ? "CA$" : cur === "USD" ? "US$" : "$";
  const rest = COUNTRIES.filter((c) => c.code !== "CA" && c.code !== "US").slice().sort((a, b) => a.name.localeCompare(b.name));
  const orderedCountries = [{ code: "CA", name: "Canada" }, { code: "US", name: "United States" }, ...rest];

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy flex items-center gap-2"><CreditCard className="h-7 w-7 text-[#202123]" /> {t("billing.title")}</h1>
        <p className="text-slatebody mt-1">{t("billing.subtitle")}</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-status">
        <div>
          <p className="font-mono text-[11px] uppercase text-slatebody">{t("billing.currentStatus")}</p>
          <p className="font-display text-lg font-semibold text-navy capitalize">
            {data.subscription_status === "trialing" ? t("billing.freeTrialDays", { n: data.trial_days_left })
              : data.subscription_status === "read_only" ? (user?.payment_suspended ? t("billing.suspended") : t("billing.trialEnded"))
              : t("billing.activePlan", { plan: PLAN_LABELS[data.plan] || data.plan })}
          </p>
          {isActive && (user?.account_type === "individual" || data.is_org_admin) && (
            user?.cancel_at_period_end
              ? <p className="text-xs text-amber-700 mt-1" data-testid="cancel-scheduled">{t("billing.cancelsEnd")}</p>
              : <button onClick={() => setCancelOpen(true)} className="text-xs text-red-600 hover:underline mt-1" data-testid="cancel-plan-btn">{t("billing.cancelPlan")}</button>
          )}
        </div>
        {!data.usage.unlimited && (
          <div className="text-right">
            <p className="font-mono text-[11px] uppercase text-slatebody">{t("billing.reportGenerations")}</p>
            <p className="font-mono text-navy" data-testid="usage-display">{t("billing.usageLeft", { dayLeft: data.usage.day_left, dayLimit: data.usage.day_limit, weekLeft: data.usage.week_left, weekLimit: data.usage.week_limit })}</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="billing-currency">
        <Label className="text-xs">{t("billing.billingCountry")}</Label>
        <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="billing-country-select"
          className="mt-1.5 block h-10 rounded-md border border-[#C5C5D0] bg-white px-3 text-sm min-w-[240px]">
          <option value="">{t("billing.selectCountryPlaceholder")}</option>
          {orderedCountries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          <option value="XX">{t("auth.countryOther")}</option>
        </select>
        {country
          ? <p className="text-sm text-navy mt-2" data-testid="billing-currency-line">{t("billing.chargedIn", { cur })}</p>
          : <p className="text-xs text-slatebody mt-2" data-testid="billing-country-helper">{t("billing.selectCountryHelper")}</p>}
      </div>

      {!isOrgAdmin && (
        <div className="grid md:grid-cols-2 gap-5">
          <PlanCard title={t("billing.monthly")} price={`${sym}3.99`} period={`/month ${cur || ""}`} testid="plan-individual-monthly"
            features={[t("billing.featUnlimited"), t("billing.featReports"), t("billing.featStyles"), t("billing.featExport")]}
            cta={isActive && data.plan === "individual_monthly" ? t("billing.currentPlan") : t("billing.chooseMonthly")}
            disabled={busy || !country || (isActive && data.plan === "individual_monthly")} busy={busy === "individual_monthly"}
            busyText={t("billing.redirecting")} blockedMsg={!country ? t("billing.selectCountryHelper") : ""}
            onClick={() => checkout("individual_monthly")} />
          <PlanCard title={t("billing.annual")} price={`${sym}39`} period={`/year ${cur || ""}`} highlight badge={t("billing.saveBadge")} testid="plan-individual-yearly"
            features={[t("billing.featEverythingMonthly"), t("billing.featTwoMonths"), t("billing.featPriority")]}
            cta={isActive && data.plan === "individual_yearly" ? t("billing.currentPlan") : t("billing.chooseAnnual")}
            disabled={busy || !country || (isActive && data.plan === "individual_yearly")} busy={busy === "individual_yearly"}
            busyText={t("billing.redirecting")} blockedMsg={!country ? t("billing.selectCountryHelper") : ""}
            onClick={() => checkout("individual_yearly")} />
        </div>
      )}

      {isOrgAdmin && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 max-w-2xl" data-testid="org-plan-card">
          <div className="flex items-center gap-2 mb-1"><Building2 className="h-5 w-5 text-[#202123]" /><h2 className="font-display text-lg font-semibold text-navy">{t("billing.orgPlan")}</h2></div>
          <p className="text-sm text-slatebody mb-6">{t("billing.orgPlanDesc")}</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[{ r: "1–20", p: 48 }, { r: "21–100", p: 40 }, { r: "101+", p: 36 }].map((b) => (
              <div key={b.r} className={`rounded-md border p-3 text-center ${seatPrice === b.p ? "border-[#C5C5D0] bg-accent" : "border-slate-200"}`}>
                <p className="font-mono text-xs text-slatebody">{t("billing.seatsRange", { r: b.r })}</p>
                <p className="font-display font-bold text-navy">{sym}{b.p}<span className="text-xs font-normal text-slatebody">{t("billing.perSeatYr")}</span></p>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-4 mb-6">
            <div className="flex-1">
              <Label>{t("billing.seats")} <span className="text-slatebody font-normal">{t("billing.seatsHave", { count: data.member_count })}</span></Label>
              <Input type="number" min="1" value={seats}
                onChange={(e) => { const v = e.target.value; if (v === "") { setSeats(""); return; } const n = parseInt(v, 10); if (!isNaN(n)) setSeats(n); }}
                onBlur={() => { if (seats === "" || Number(seats) < 1) setSeats(Math.max(1, data.member_count || 1)); }}
                className="mt-1.5 font-mono" data-testid="seat-input" />
            </div>
            <div className="text-right">
              <p className="font-mono text-[11px] uppercase text-slatebody">{t("billing.totalYear")}</p>
              <p className="font-display text-2xl font-bold text-navy" data-testid="org-total">{sym}{(seats * seatPrice).toLocaleString()} <span className="text-xs font-normal text-slatebody">{cur || ""}</span></p>
            </div>
          </div>
          <Button className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" disabled={busy || !country} onClick={() => checkout("organization")} data-testid="org-checkout-btn">
            <Sparkles className="h-4 w-4" /> {busy === "organization" ? t("billing.redirecting") : t("billing.subscribeSeats", { count: seats || 1 })}
          </Button>
          {!country && <p className="text-xs text-slatebody mt-2" data-testid="org-country-helper">{t("billing.selectCountryHelper")}</p>}
          <p className="text-sm text-slatebody mt-3 leading-relaxed" data-testid="billing-org-story-msg">{t("billing.orgStoryMsg")}</p>
        </div>
      )}
      {dunning && dunning.members && dunning.members.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-6 md:p-8 mt-6" data-testid="org-dunning-digest">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-5 w-5 text-red-700" />
            <h2 className="font-display text-lg font-semibold text-red-800">{t("billing.teamBillingTitle")}</h2>
          </div>
          <p className="text-sm text-red-700/90 mb-4">
            {dunning.suspended ? t("billing.teamBillingSuspendedDesc") : t("billing.teamBillingPastDueDesc")}
            {dunning.retry_at && !dunning.suspended && (
              <> {t("billing.teamBillingRetry", { date: new Date(dunning.retry_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) })}</>
            )}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-red-700/80 border-b border-red-100">
                <th className="p-2.5 font-medium">{t("billing.colMember")}</th>
                <th className="p-2.5 font-medium">{t("billing.colEmail")}</th>
                <th className="p-2.5 font-medium">{t("billing.colStatus")}</th>
              </tr></thead>
              <tbody>
                {dunning.members.map((m) => (
                  <tr key={m.id} className="border-b border-red-50" data-testid={`dunning-member-${m.id}`}>
                    <td className="p-2.5 text-navy">{m.full_name || "—"}</td>
                    <td className="p-2.5 text-slatebody">{m.email}</td>
                    <td className="p-2.5">
                      {m.suspended
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-red-200 text-red-800"><XCircle className="h-3 w-3" /> {t("billing.statusSuspended")}</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-amber-200 text-amber-800"><AlertTriangle className="h-3 w-3" /> {t("billing.statusPastDue")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {history.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 mt-6" data-testid="billing-history">
          <div className="flex items-center gap-2 mb-4"><Receipt className="h-5 w-5 text-[#202123]" /><h2 className="font-display text-lg font-semibold text-navy">{t("billing.billingHistory")}</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slatebody border-b border-slate-100">
                <th className="p-2.5 font-medium">{t("billing.colDate")}</th><th className="p-2.5 font-medium">{t("billing.colPlan")}</th>
                <th className="p-2.5 font-medium">{t("billing.colSeats")}</th><th className="p-2.5 font-medium">{t("billing.colAmount")}</th>
                <th className="p-2.5 font-medium">{t("billing.colStatus")}</th><th className="p-2.5 font-medium">{t("billing.colReceipt")}</th>
              </tr></thead>
              <tbody>
                {history.map((tx) => (
                  <tr key={tx.session_id} className="border-b border-slate-50" data-testid={`txn-${tx.session_id}`}>
                    <td className="p-2.5 text-navy">{tx.created_at ? new Date(tx.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}</td>
                    <td className="p-2.5 text-navy">{PLAN_LABELS[tx.plan] || tx.plan}</td>
                    <td className="p-2.5 text-slatebody">{tx.seats || 1}</td>
                    <td className="p-2.5 font-mono text-navy">{tx.amount != null ? `${(tx.currency || "usd").toUpperCase() === "CAD" ? "CA$" : "US$"}${tx.amount.toFixed(2)} ${(tx.currency || "usd").toUpperCase()}` : "—"}</td>
                    <td className="p-2.5"><span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${(tx.payment_status === "paid" || tx.status === "completed") ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{tx.payment_status || tx.status}</span></td>
                    <td className="p-2.5">
                      {(tx.payment_status === "paid" || tx.status === "completed")
                        ? <button onClick={() => openReceipt(tx)} className="text-[#202123] hover:underline inline-flex items-center gap-1 text-sm" data-testid={`receipt-${tx.session_id}`}><Download className="h-3.5 w-3.5" /> {t("billing.receipt")}</button>
                        : <span className="text-slatebody text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {data.stripe_mode !== "live" && (
        <p className="text-xs text-slatebody mt-6 font-mono" data-testid="billing-test-mode-banner">{t("billing.testMode")}</p>
      )}

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-white" data-testid="cancel-plan-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("billing.cancelTitle")}</DialogTitle>
            <DialogDescription>{t("billing.cancelDesc")}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="cancel-plan-keep">{t("billing.keepPlan")}</Button>
            <Button className="bg-red-800 hover:bg-red-900 text-white" disabled={cancelBusy} onClick={cancelPlan} data-testid="cancel-plan-confirm">{cancelBusy ? t("billing.cancelling") : t("billing.cancelPlan")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanCard({ title, price, period, features, cta, onClick, disabled, busy, busyText, highlight, badge, testid, blockedMsg }) {
  return (
    <div className={`rounded-xl border bg-white p-6 md:p-8 ${highlight ? "border-[#C5C5D0] ring-1 ring-[#C5C5D0]/50" : "border-slate-200"}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-lg font-semibold text-navy">{title}</h3>
        {badge && <span className="text-[11px] rounded-full bg-accent px-2 py-0.5 text-accent-foreground font-medium">{badge}</span>}
      </div>
      <p className="font-display text-3xl font-bold text-navy mb-5">{price}<span className="text-sm font-normal text-slatebody">{period}</span></p>
      <ul className="space-y-2.5 mb-6">
        {features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-navy"><Check className="h-4 w-4 text-[#202123] shrink-0" /> {f}</li>)}
      </ul>
      <Button className={`w-full ${highlight ? "bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" : ""}`} variant={highlight ? "default" : "outline"} disabled={disabled} onClick={onClick} data-testid={`${testid}-btn`}>
        {busy ? busyText : cta}
      </Button>
      {blockedMsg && <p className="text-xs text-slatebody mt-2 text-center" data-testid={`${testid}-helper`}>{blockedMsg}</p>}
    </div>
  );
}
